// src/components/sessions/modals/ManageTeachersModal.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { XCircleIcon } from '@heroicons/react/24/outline';

const ManageTeachersModal = ({
  isOpen,
  onClose,
  isOwner,
  teacherError,
  teacherSearchQuery,
  setTeacherSearchQuery,
  teacherTabView,
  setTeacherTabView,
  filteredCourseTeachers,
  filteredAvailableTeachers,
  currentUserInstitutionMemberId,
  handleAddTeacher,
  handleRemoveTeacher
}) => {
  const { t } = useTranslation();

  if (!isOpen || !isOwner) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">
              {t('sessions.modals.manageTeachers.title')}
            </h3>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600"
              aria-label={t('common.close')}
            >
              <XCircleIcon className="w-6 h-6" />
            </button>
          </div>
          <p className="text-xs text-amber-700 mt-2">
            {t('sessions.modals.manageTeachers.adminHint')}
          </p>
        </div>

        <div className="p-6">
          {teacherError && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
              {teacherError}
            </div>
          )}

          <div className="mb-4">
            <input
              type="text"
              placeholder={t('sessions.modals.manageTeachers.searchPlaceholder')}
              value={teacherSearchQuery}
              onChange={(e) => setTeacherSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          <div className="mb-4">
            <div className="flex space-x-1 border-b border-slate-200">
              <button
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  teacherTabView === 'current'
                    ? 'text-cyan-600 border-b-2 border-cyan-600'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                onClick={() => setTeacherTabView('current')}
              >
                {t('sessions.modals.manageTeachers.tabs.current', { count: filteredCourseTeachers.length })}
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  teacherTabView === 'available'
                    ? 'text-cyan-600 border-b-2 border-cyan-600'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                onClick={() => setTeacherTabView('available')}
              >
                {t('sessions.modals.manageTeachers.tabs.available', { count: filteredAvailableTeachers.length })}
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {teacherTabView === 'current' ? (
              filteredCourseTeachers.length === 0 ? (
                <p className="text-center text-slate-500 py-8">
                  {teacherSearchQuery 
                    ? t('sessions.modals.manageTeachers.noSearchResults') 
                    : t('sessions.modals.manageTeachers.noCurrentTeachers')}
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredCourseTeachers.map(teacher => (
                    <div key={teacher.institution_member_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-800">
                          {teacher.display_name || teacher.full_name || teacher.email}
                          {teacher.is_owner && (
                            <span className="ml-2 text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                              {t('roles.owner')}
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-slate-600">{teacher.email}</p>
                      </div>
                      {!teacher.is_owner && teacher.institution_member_id !== currentUserInstitutionMemberId && (
                        <button
                          onClick={() => handleRemoveTeacher(teacher.institution_member_id)}
                          className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        >
                          {t('sessions.modals.manageTeachers.remove')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )
            ) : (
              filteredAvailableTeachers.length === 0 ? (
                <p className="text-center text-slate-500 py-8">
                  {teacherSearchQuery 
                    ? t('sessions.modals.manageTeachers.noSearchResults') 
                    : t('sessions.modals.manageTeachers.noAvailableTeachers')}
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredAvailableTeachers.map(teacher => (
                    <div key={teacher.institution_member_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-800">
                          {teacher.display_name || teacher.full_name || teacher.email}
                        </p>
                        <p className="text-sm text-slate-600">{teacher.email}</p>
                      </div>
                      <button
                        onClick={() => handleAddTeacher(teacher.institution_member_id)}
                        className="px-3 py-1 text-sm text-cyan-600 hover:bg-cyan-50 rounded-md transition-colors"
                      >
                        {t('sessions.modals.manageTeachers.add')}
                      </button>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageTeachersModal;